import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import AlbumsIcon from '@mui/icons-material/AccountTreeOutlined'
import AllItemsIcon from '@mui/icons-material/PhotoLibraryOutlined'
import { GroupsOutlined } from '@mui/icons-material'
import { Box, ButtonBase, styled } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { useNavigate, useLocation, useMatch } from 'react-router-dom'
import { drawerVar } from '../../../../providers/apollo/cache'
import UploadIconButton from './UploadIconButton'
import { UserProfileDocument } from '../../../../graphql/generated'
import useSelectedAlbums from '../../hooks/useSelectedAlbums'
import useSelectedMedia from '../../hooks/useSelectedMedia'
import useBucket from '../../hooks/useBucket'

// Styles for descendants of the listItem
export const listItemSx = {
  '& .MuiListItemIcon-root': { minWidth: '36px' },
  '& .MuiListItemButton-root': { borderRadius: '4px', padding: '8px 12px' }
}

const PillWrapper = styled(Box)(({ theme }: { theme: Theme }) => ({
  position: 'fixed',
  bottom: '16px',
  right: '32px',
  backgroundColor: '#F6F6F6',
  borderRadius: '100px',
  display: 'flex',
  flexDirection: 'row',
  gap: '32px',
  zIndex: 1,
  // Animation styles
  animation: 'rotateOutVerticalSmooth 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
  '@keyframes rotateOutVerticalSmooth': {
    '0%': {
      opacity: 0,
      transform: 'perspective(600px) rotateX(90deg) translateX(0)'
    },
    '100%': {
      opacity: 1,
      transform: 'perspective(600px) rotateX(0deg) translateX(0)'
    }
  },
  [theme.breakpoints.down('sm')]: {
    bottom: '8px',
    right: 'auto',
    left: '50%',
    transform: 'translateX(-50%)',
    margin: '0 auto',
    width: 'calc(100% - 16px)',
    gap: 0,
    justifyContent: 'space-between',
    maxWidth: '420px',
    // Override animation for mobile to prevent skewing off screen
    animation: 'rotateOutVerticalSmoothMobile 0.5s cubic-bezier(0.23, 1, 0.32, 1)',
    '@keyframes rotateOutVerticalSmoothMobile': {
      '0%': {
        opacity: 0,
        transform: 'translateX(-50%) perspective(600px) rotateX(90deg)'
      },
      '100%': {
        opacity: 1,
        transform: 'translateX(-50%) perspective(600px) rotateX(0deg)'
      }
    }
  }
}))

const NavButton = styled(ButtonBase)({
  flexDirection: 'column',
  gap: '8px',
  fontWeight: 400,
  fontSize: '1rem',
  lineHeight: '1.5',
  padding: '0 4px',
  borderRadius: '6px',
  fontFamily: 'Outfit, sans-serif',
  '&:hover': {
    color: '#1C47CB'
  },
  ':disabled': {
    opacity: 0.3
  }
})

const ROUTES = {
  bucket: (id: string | undefined) => `/bucket/${id}`,
  albums: (id: string | undefined) => `/bucket/${id}/albums`,
  people: (id: string | undefined) => `/bucket/${id}/manage`, // remove when sharing page is enabled
  sharing: (id: string | undefined) => `/bucket/${id}/sharing`
}

type RouteKey = keyof typeof ROUTES

export default function NavigationPill() {
  const { bucket, bucketUser } = useBucket()
  const { data } = useQuery(UserProfileDocument)
  const bucketId = bucket?.id ? bucket?.id : data?.userProfile?.currentBucketId || ''
  const navigate = useNavigate()
  const location = useLocation()
  const permissions = bucketUser?.permissions
  const manageAllowed = !!(permissions?.userList && bucketId && !bucket?.isBanned)
  const { selectedModeEnabled: selectedModeEnabledAlbums } = useSelectedAlbums()
  const { selectedModeEnabled: selectedModeEnabledMedia } = useSelectedMedia()
  const selectedModeEnabled = selectedModeEnabledAlbums || selectedModeEnabledMedia
  const bucketUrl = useMatch('/bucket/:bucketId/:slug')
  const settingsUrl = useMatch('/settings')
  const isUploadHidden = !!(bucketUrl?.params.slug === 'trash' || bucketUrl?.params.slug === 'manage' || settingsUrl)

  const isCurrentRoute = useMemo(() => {
    return (route: string) => location.pathname === route
  }, [location])

  const handleNavigation = (path: RouteKey) => () => {
    const route = ROUTES[path](bucketId)
    if (route) {
      navigate(route)
      drawerVar(null)
    }
  }

  if (!bucketId) {
    return null
  }

  return (
    <PillWrapper sx={{ padding: isUploadHidden ? '16px 32px' : '8px 16px 8px 32px', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)' }}>
      <NavButton
        onClick={handleNavigation('bucket')}
        sx={{
          color: isCurrentRoute(ROUTES.bucket(bucketId)) ? '#1C47CB' : '#727C8C',
          fontWeight: 500,
          fontSize: '0.875rem'
        }}
      >
        <AllItemsIcon />
        Gallery
      </NavButton>
      <NavButton
        onClick={handleNavigation('albums')}
        sx={{
          color: isCurrentRoute(ROUTES.albums(bucketId)) ? '#1C47CB' : '#727C8C',
          fontWeight: 500,
          fontSize: '0.875rem'
        }}
      >
        <AlbumsIcon />
        Albums
      </NavButton>
      <NavButton
        onClick={handleNavigation('sharing')}
        disabled={!manageAllowed}
        sx={{
          color: isCurrentRoute(ROUTES.sharing(bucketId)) ? '#1C47CB' : '#727C8C',
          fontWeight: 500,
          fontSize: '0.875rem'
        }}
      >
        <GroupsOutlined />
        Sharing
      </NavButton>
      {!isUploadHidden && <UploadIconButton selectModeEnabled={selectedModeEnabled} />}
    </PillWrapper>
  )
}
